import { api } from './index';

export interface PulseReportApiMember {
  fullName: string;
  avatarUrl?: string | null;
}

export type PulseDebriefStatus = 'hidden' | 'pending' | 'debriefed' | 'expired';

export type PulseRelationshipStage = 'none' | 'chemistry_only' | 'selected';

export interface PulseReportApiDebrief {
  status: PulseDebriefStatus;
  debriefedAt?: string | null;
  debriefedById?: string | null;
  debriefedBy?: PulseReportApiMember | null;
  selectedAt?: string | null;
  bannerWindowStartsAt?: string | null;
  bannerWindowExpiresAt?: string | null;
  canMarkDebriefed: boolean;
  relationshipStage: PulseRelationshipStage;
}

export type PulseReportApiAnswer =
  | {
      kind: 'range10';
      questionId: string;
      label: string;
      value: number;
    }
  | {
      kind: 'freeform';
      questionId: string;
      label: string;
      text: string;
    }
  | {
      kind: 'multi_select';
      questionId: string;
      label: string;
      options: string[];
    }
  | {
      kind: 'rating';
      questionId: string;
      label: string;
      value: number;
      max: number;
    };

export interface PulseReportApiSection {
  key: string;
  title: string;
  coachNameAtSubmission?: string;
  answers: PulseReportApiAnswer[];
}

export interface PulseReportApiSource {
  formType?: string;
}

export type PulseReportKind = 'pulse_check' | 'onboarding_baseline';

export interface PulseReportApiReport {
  reportId: string;
  memberId: string;
  member: PulseReportApiMember;
  submittedAt: string;
  sourceVersion: string;
  source?: PulseReportApiSource;
  reportKind?: PulseReportKind;
  isOnboardingBaseline?: boolean;
  sections: PulseReportApiSection[];
  debrief?: PulseReportApiDebrief | null;
}

export type OnboardingReviewAnswerValue = string | number | boolean | string[];

export interface OnboardingReviewApiAnswer {
  id: string;
  fieldRef: string;
  questionKey: string;
  prompt: string;
  kind: string;
  value: OnboardingReviewAnswerValue;
}

export interface OnboardingReviewApiSection {
  key: string;
  title: string;
  order: number;
  answers: OnboardingReviewApiAnswer[];
}

export interface OnboardingReviewApiPayload {
  kind: 'onboarding_review';
  schemaVersion: 'member-onboarding-review-v1';
  id: string;
  pulseReportId: string;
  title: string;
  submittedAt: string;
  source: {
    formType: 'member_onboarding';
    vendor: 'typeform';
    schemaVersion: 'member-onboarding-review-v1';
  };
  pulseSections?: PulseReportApiSection[];
  sections: OnboardingReviewApiSection[];
}

export interface OnboardingReviewResponse {
  review: OnboardingReviewApiPayload;
}

export interface PulseReportApiPagination {
  currentPage: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
}

export interface PulseReportDetailResponse {
  report: PulseReportApiReport;
  previousReport?: PulseReportApiReport | null;
}

export interface PulseReportListResponse {
  reports: PulseReportApiReport[];
  pagination: PulseReportApiPagination;
}

export interface PulsePendingDebriefBanner {
  reportId: string;
  memberId: string;
  memberName: string;
  submittedAt: string;
  bannerWindowExpiresAt: string;
}

export interface PulsePendingDebriefsResponse {
  banners: PulsePendingDebriefBanner[];
}

export interface GetPulseReportsParams {
  memberId: string;
  page?: number;
  per?: number;
  formType?: string;
  onboardingReviewAvailable?: boolean;
}

export const pulseApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOnboardingReview: builder.query<OnboardingReviewResponse, string>({
      providesTags: (_result, _error, id) => [{ id, type: 'PulseReport' }],
      query: (id) => ({
        method: 'GET',
        url: `/pulse/reports/${id}/onboarding_review`,
      }),
    }),
    getPendingPulseDebriefs: builder.query<PulsePendingDebriefsResponse, void>({
      providesTags: ['PulseReport'],
      query: () => ({
        method: 'GET',
        url: '/pulse/pending_debriefs',
      }),
    }),
    getPulseReport: builder.query<PulseReportDetailResponse, string>({
      providesTags: (_result, _error, id) => [{ id, type: 'PulseReport' }],
      query: (id) => ({
        method: 'GET',
        url: `/pulse/reports/${id}`,
      }),
    }),
    getPulseReports: builder.query<
      PulseReportListResponse,
      GetPulseReportsParams
    >({
      providesTags: ['PulseReport'],
      query: ({
        formType,
        memberId,
        onboardingReviewAvailable,
        page = 1,
        per = 10,
      }) => ({
        method: 'GET',
        params: {
          formType,
          memberId,
          onboardingReviewAvailable,
          page,
          per,
        },
        url: '/pulse/reports',
      }),
    }),
    markPulseReportDebriefed: builder.mutation<
      { report: PulseReportApiReport },
      string
    >({
      invalidatesTags: (_result, _error, id) => [
        { id, type: 'PulseReport' },
        'PulseReport',
      ],
      query: (id) => ({
        method: 'POST',
        url: `/pulse/reports/${id}/debrief`,
      }),
    }),
  }),
});

export const {
  useGetOnboardingReviewQuery,
  useGetPendingPulseDebriefsQuery,
  useGetPulseReportQuery,
  useGetPulseReportsQuery,
  useMarkPulseReportDebriefedMutation,
} = pulseApi;
